'use client'

import React, { useEffect, useState, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/store'
import { setLoginMessageShown } from '@/store/member/slice'
import { SESSION_STORAGE_KEYS, LOCAL_STORAGE_KEYS } from '@/constants/storageKeys'
import { SUCCESS_MESSAGES } from '@/constants/messages'
import styles from './styles.module.scss'

interface MessageAfterLoginProps {
  memberId: number | null
}

/**
 * Component that displays a message to the user after successful login
 * Based on implementation using sessionStorage for reliable state management
 */
export default function MessageAfterLogin({ memberId }: MessageAfterLoginProps) {
  const pathname = usePathname()
  const dispatch = useDispatch<AppDispatch>()
  const [showMessage, setShowMessage] = useState(false)
  const [messageType, setMessageType] = useState<
    'login' | 'registration' | 'quit' | 'unauthorized' | null
  >(null)
  const cleanupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Get sessionStorage value
  const getSessionItem = (name: string): string | null | undefined => {
    if (typeof window === 'undefined') return null
    try {
      return sessionStorage.getItem(name)
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('SessionStorage get error:', err)
      }
    }
  }

  // Remove sessionStorage item
  const removeSessionItem = (name: string) => {
    if (typeof window === 'undefined') return
    try {
      sessionStorage.removeItem(name)
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('SessionStorage remove error:', err)
      }
    }
  }

  // Get localStorage
  const getLocalItem = (name: string): string | null | undefined => {
    if (typeof window === 'undefined') return null
    try {
      return localStorage.getItem(name)
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('LocalStorage get error:', err)
      }
    }
  }

  // Remove localStorage
  const removeLocalItem = (name: string) => {
    if (typeof window === 'undefined') return
    try {
      localStorage.removeItem(name)
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('LocalStorage remove error:', err)
    }
  }

  useEffect(() => {
    // レイアウトマウント時にsessionStorage/localStorageをチェック
    const checkMessages = () => {
      const loginSuccess = getSessionItem(SESSION_STORAGE_KEYS.EMAIL_LOGIN)
      const quitMessage = getSessionItem('quitMessage')
      const unauthorizedError = getSessionItem(SESSION_STORAGE_KEYS.UNAUTHORIZED_ERROR)
      const snsLogin = getLocalItem(LOCAL_STORAGE_KEYS.SNS_LOGIN)
      const additionalInfoCompleted = getSessionItem('additional_info_completed')

      // localStorageにSNSLoginがあり、memberIdがnullの場合は2s待つ
      if (snsLogin && !memberId) {
        if (cleanupTimerRef.current) {
          clearTimeout(cleanupTimerRef.current)
        }
        cleanupTimerRef.current = setTimeout(() => {
          // 2s後もmemberIdがnullならキャンセルと判断してlocalStorage削除
          if (!memberId) {
            removeLocalItem(LOCAL_STORAGE_KEYS.SNS_LOGIN)
            removeLocalItem(LOCAL_STORAGE_KEYS.BEFORE_PATH)
          }
        }, 2000)
        return
      }

      // memberIdが取得できた場合、待機中のタイマーキャンセル
      if (cleanupTimerRef.current) {
        clearTimeout(cleanupTimerRef.current)
        cleanupTimerRef.current = null
      }

      // additional_info完了メッセージ
      if (additionalInfoCompleted) {
        setMessageType('registration')
        setShowMessage(true)
        dispatch(setLoginMessageShown(true))

        // sessionStorageフラグを削除(beforePathはredirect用に残す)
        removeSessionItem('additional_info_completed')
        removeSessionItem(SESSION_STORAGE_KEYS.EMAIL_LOGIN)

        // 1.5s後に非表示＆snsLoginフラグ削除（ログインメッセージの重複表示を防止）
        setTimeout(() => {
          setShowMessage(false)
          dispatch(setLoginMessageShown(false))
          removeLocalItem(LOCAL_STORAGE_KEYS.SNS_LOGIN)
        }, 1500)
      }
      // SNSログイン成功メッセージ
      // ※beforePathが存在する場合も表示し、メッセージ閉じた後にMypageClientがリダイレクト
      else if (memberId && snsLogin) {
        setMessageType('login')
        setShowMessage(true)
        dispatch(setLoginMessageShown(true))

        setTimeout(() => {
          setShowMessage(false)
          dispatch(setLoginMessageShown(false))
          removeLocalItem(LOCAL_STORAGE_KEYS.SNS_LOGIN)
        }, 1500)
      }
      // メールログイン成功メッセージ
      else if (loginSuccess) {
        setMessageType('login')
        setShowMessage(true)
        dispatch(setLoginMessageShown(true))

        // 1.5s後に自動非表示
        // ※フラグ削除はMypageClientのリダイレクト処理で行う
        setTimeout(() => {
          setShowMessage(false)
          dispatch(setLoginMessageShown(false))
        }, 1500)
      }
      // 退会メッセージ
      else if (quitMessage) {
        setMessageType('quit')
        setShowMessage(true)

        removeSessionItem('quitMessage')

        // Auto-hide after 3.5 seconds
        setTimeout(() => {
          setShowMessage(false)
        }, 3500)
      }
      // 認証切れメッセージ
      else if (unauthorizedError) {
        setMessageType('unauthorized')
        setShowMessage(true)

        removeSessionItem(SESSION_STORAGE_KEYS.UNAUTHORIZED_ERROR)

        // Auto-hide after 3 seconds
        setTimeout(() => {
          setShowMessage(false)
        }, 3000)
      }
    }

    // マウント時に即座にチェック
    checkMessages()

    // クリーンアップ
    return () => {
      if (cleanupTimerRef.current) {
        clearTimeout(cleanupTimerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, pathname])

  if (!showMessage) return null

  return (
    <div className={styles.messageOverlay}>
      <div className={styles.messageWindow}>
        <dl className={styles.messageContentsWrapper}>
          <dt className={styles.messageImage}>
            <img
              src='/images/svg/logo_icon.svg'
              className={styles.logoImg}
              alt='logo'
              loading='lazy'
            />
          </dt>
          <dd className={styles.messageContents}>
            <div className={styles.messageInner}>
              {messageType === 'login' && (
                <>
                  <p className={styles.messageTitle}>ログインしました</p>
                  <p className={styles.messageText}>ページを表示する準備をしています。</p>
                </>
              )}
              {messageType === 'registration' && (
                <>
                  <p className={styles.messageTitle}>{SUCCESS_MESSAGES.REGISTRATION_COMPLETE}</p>
                  <p className={styles.messageText}>ページを表示する準備をしています。</p>
                </>
              )}
              {messageType === 'quit' && (
                <>
                  <p className={styles.messageTitle}>{SUCCESS_MESSAGES.QUIT_COMPLETE}</p>
                  <p className={styles.messageText}>今までのご利用ありがとうございました。</p>
                </>
              )}
              {messageType === 'unauthorized' && (
                <p className={styles.messageTitle}>
                  ログイン状態が切れましたので、再度ログインお願いします。
                </p>
              )}
            </div>
          </dd>
        </dl>
      </div>
    </div>
  )
}
